import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  useDispensarSancoes,
  useFecharCompetencia,
  useReabrirCompetencia,
  useRevogarDispensa,
} from '../../../api/queries/chamados'
import { useSessao } from '../../../auth/contexto'
import { podeFiscalizar } from '../../../auth/permissoes'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro } from '../../../components/Estados'
import { TEXTOS } from '../../../lib/glossario'
import type { DispensaRegistrada, SituacaoDaCompetencia } from '../../../types/chamado'
import { competenciaPorExtenso } from '../competencia'
import { competenciaAtual } from '../../repositorios/setores'
import { DialogoDeConfirmacao } from './DialogoDeConfirmacao'
import { enderecoDaFilaDeJulgamento } from './indicadores'
import { DialogoDeTexto } from './DialogoDeTexto'

/**
 * Os atos do fiscal sobre a competência: fechar, reabrir, dispensar.
 *
 * **Fechar pede confirmação e diz o que muda.** Não porque seja irreversível — reabrir existe —,
 * mas porque o que muda não é óbvio: a partir do fechamento, marcação nova é recusada, e o fiscal
 * que estivesse no meio do julgamento descobriria isso pelo erro.
 *
 * **Os botões respeitam o papel, e não só o lado do contrato.** A recusa de verdade é do servidor
 * (`@PreAuthorize(FISCAL)`), e ela vale de qualquer jeito; o que se evita aqui é oferecer a alguém
 * de "somente consulta" um botão que só produziria um 403 — o mesmo princípio de
 * `/transicoes-possiveis`, que não mostra ação que o domínio vai negar.
 *
 * **Reabrir só aparece para administrador.** A assimetria entre quem fecha e quem reabre é o que
 * faz a reabertura ser uma conversa em vez de um clique.
 *
 * @param props o estado da competência e a dispensa vigente
 * @returns a barra de ações
 */
export function AcoesDaCompetencia({
  competencia,
  situacao,
  dispensa,
  chamadosSemJulgamento,
}: {
  competencia: string
  situacao: SituacaoDaCompetencia
  dispensa: DispensaRegistrada | null
  chamadosSemJulgamento: number
}) {
  const { usuario } = useSessao()
  const fechar = useFecharCompetencia(competencia)
  const reabrir = useReabrirCompetencia(competencia)
  const dispensar = useDispensarSancoes(competencia)
  const revogar = useRevogarDispensa(competencia)
  const [pedindo, setPedindo] = useState<'reabrir' | 'dispensa' | 'fechar' | null>(null)

  const fechada = situacao.situacao === 'FECHADA'
  // Reabrir é `ADMIN_DO_CONTRATANTE` no servidor — as DUAS chaves, desde a correção do A6. Um
  // ADMIN da contratada não vê o botão pela mesma razão que não passa pelo endpoint.
  const ehAdmin = podeFiscalizar(usuario) && (usuario?.papeis.includes('ADMIN') ?? false)
  // Fechar e dispensar são atos do fiscal. LEITOR e membro do comitê enxergam a apuração — e
  // devem enxergar —, mas não praticam o ato. O critério mora em `auth/permissoes`: era daqui
  // que ele precisava sair para valer também nos outros cinco pontos da tela.
  const podeFechar = podeFiscalizar(usuario)
  // Mês em curso não fecha: o servidor recusa, e o botão diz por quê antes do clique.
  const emCurso = competencia >= competenciaAtual()
  const erro = fechar.error ?? reabrir.error ?? dispensar.error ?? revogar.error
  const ocupado = fechar.isPending || reabrir.isPending || dispensar.isPending || revogar.isPending

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {!fechada && podeFechar && (
          <Botao
            aparencia="primario"
            onClick={() => setPedindo('fechar')}
            disabled={ocupado || chamadosSemJulgamento > 0 || emCurso}
          >
            {fechar.isPending ? TEXTOS.fiscalizacao.acoes.fechando : TEXTOS.fiscalizacao.acoes.fechar}
          </Botao>
        )}

        {fechada && ehAdmin && (
          <Botao onClick={() => setPedindo('reabrir')} disabled={ocupado}>
            {TEXTOS.fiscalizacao.acoes.reabrir}
          </Botao>
        )}

        {!fechada && podeFechar && !dispensa && (
          <Botao onClick={() => setPedindo('dispensa')} disabled={ocupado}>
            {TEXTOS.fiscalizacao.dispensa.registrar}
          </Botao>
        )}

        {!fechada && podeFechar && dispensa && (
          <Botao onClick={() => revogar.mutate('')} disabled={ocupado}>
            {TEXTOS.fiscalizacao.dispensa.revogar}
          </Botao>
        )}
      </div>

      {/* O motivo do bloqueio é escrito, e não deixado no botão cinza: "por que não posso fechar?"
          é a pergunta que gera chamado no suporte. */}
      {!fechada && chamadosSemJulgamento > 0 && (
        <p className="max-w-prose text-sm text-amber-800">
          {TEXTOS.fiscalizacao.acoes.faltaJulgar(chamadosSemJulgamento)}{' '}
          <Link to={enderecoDaFilaDeJulgamento(competencia)} className="font-medium underline underline-offset-2">
            {TEXTOS.fiscalizacao.semJulgamento.verAFila}
          </Link>
        </p>
      )}
      {!fechada && emCurso && (
        <p className="max-w-prose text-sm text-slate-600">
          {TEXTOS.fiscalizacao.acoes.mesEmCurso(competenciaPorExtenso(competencia))}
        </p>
      )}

      {erro && <AvisoDeErro erro={erro} titulo={TEXTOS.fiscalizacao.acoes.naoDeuCerto} />}

      {pedindo === 'fechar' && (
        <DialogoDeConfirmacao
          titulo={TEXTOS.fiscalizacao.acoes.fechar}
          ajuda={TEXTOS.fiscalizacao.acoes.fecharAjuda}
          confirmar={TEXTOS.fiscalizacao.acoes.fechar}
          aoConfirmar={() => {
            fechar.mutate('')
            setPedindo(null)
          }}
          aoFechar={() => setPedindo(null)}
        />
      )}

      {pedindo === 'reabrir' && (
        <DialogoDeTexto
          titulo={TEXTOS.fiscalizacao.acoes.reabrir}
          ajuda={TEXTOS.fiscalizacao.acoes.reabrirAjuda}
          rotulo="Motivo da reabertura"
          aoConfirmar={(texto) => {
            reabrir.mutate(texto)
            setPedindo(null)
          }}
          aoFechar={() => setPedindo(null)}
        />
      )}

      {pedindo === 'dispensa' && (
        <DialogoDeTexto
          titulo={TEXTOS.fiscalizacao.dispensa.registrar}
          ajuda={TEXTOS.fiscalizacao.dispensa.ajuda}
          rotulo="Fundamento da dispensa"
          aoConfirmar={(texto) => {
            dispensar.mutate(texto)
            setPedindo(null)
          }}
          aoFechar={() => setPedindo(null)}
        />
      )}
    </div>
  )
}
